import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { MetricCard, StagePill } from "./appShared";
import {
  Check,
  ChevronRight,
  Clock,
  ExternalLink,
  FileText,
  Lock,
  Target,
  TrendingUp,
  X,
} from "lucide-react";

export const TenantDashboard = ({ tenantId = "tenant_1" }) => {
  // Stable mock values per tenant (no random jitter each refresh)
  const seed = String(tenantId).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const opportunityScore = 62 + (seed % 19); // 62-80
  const workflowsIdentified = 8 + (seed % 7); // 8-14
  const workflowsPrioritized = Math.max(3, Math.min(workflowsIdentified, 4 + (seed % 6)));
  const roadmapProgress = 35 + (seed % 46); // 35-80
  const automationsLive = 1 + (seed % 5); // 1-5
  const hoursSaved = 6 + (seed % 15); // 6-20

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <MetricCard
        label="AI Opportunity Score"
        value={opportunityScore}
        sub="Based on your intake"
        accent
      />
      <MetricCard label="Workflows identified" value={workflowsIdentified} sub="High-ROI opportunities" />
      <MetricCard label="Workflows prioritized" value={workflowsPrioritized} sub="Ready for implementation" />
      <MetricCard label="Roadmap progress" value={`${roadmapProgress}%`} sub="Toward full automation" />
      <MetricCard label="Automations live" value={automationsLive} sub="Currently running" />
      <MetricCard label="Hours saved / week" value={hoursSaved} sub="Estimated impact" />
    </div>
  );
};

// Customer Portal - Full experience
export const CustomerPortal = ({ session }) => {
  const [activeTab, setActiveTab] = useState("overview");
  
  // Mock customer data - in real app, would fetch from API based on session
  const tenantId = session.tenant?.id || "tenant_1";
  const seed = String(tenantId).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  
  const customerData = {
    companyName: session.tenant?.name || "Your Company",
    currentStage: "ASSESSMENT_IN_PROGRESS",
    opportunityScore: 62 + (seed % 19),
    workflowsIdentified: 8 + (seed % 7),
    workflowsPrioritized: 4,
    nextAction: "Complete your In-Depth Assessment questionnaire",
    consultant: "Alex Morgan",
    startDate: "2026-02-15",
  };

  // Mock deliverables
  const deliverables = [
    {
      id: "opp_snapshot",
      title: "Opportunity Snapshot",
      description: "Your top 3 workflow opportunities based on intake analysis",
      status: "available",
      date: "2026-02-16",
      type: "pdf"
    },
    {
      id: "detailed_assessment",
      title: "In-Depth Assessment Report",
      description: "Comprehensive workflow analysis with prioritized recommendations",
      status: "in_progress",
      date: null,
      type: "pdf"
    },
    {
      id: "sow",
      title: "Scope of Services",
      description: "Implementation scope, timeline, and deliverables",
      status: "locked",
      date: null,
      type: "pdf"
    },
    {
      id: "value_pack",
      title: "Value Validation Pack",
      description: "KPI tracking and ROI documentation",
      status: "locked",
      date: null,
      type: "pdf"
    },
  ];

  // Mock approvals
  const pendingApprovals = [
    {
      id: "apr_1",
      title: "Workflow Automation Scope",
      description: "Approve the 3 workflows selected for Starter Implementation",
      requestedBy: "Alex Morgan",
      date: "2026-03-08",
      priority: "high"
    },
  ];

  // Progress milestones
  const milestones = [
    { label: "Intake Submitted", completed: true, date: "2026-02-15" },
    { label: "Opportunity Snapshot", completed: true, date: "2026-02-16" },
    { label: "Assessment Purchased", completed: true, date: "2026-02-20" },
    { label: "Assessment In Progress", completed: false, current: true },
    { label: "Assessment Delivered", completed: false },
    { label: "Implementation Started", completed: false },
    { label: "Value Validated", completed: false },
  ];

  const currentMilestoneIdx = milestones.findIndex(m => m.current);
  const progressPercent = Math.round((currentMilestoneIdx / (milestones.length - 1)) * 100);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Welcome back</h1>
            <p className="mt-1 text-sm text-foreground/60">
              {customerData.companyName} · Working with {customerData.consultant}
            </p>
          </div>
          <StagePill stage={customerData.currentStage} />
        </div>
        
        {/* Progress bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between text-xs text-foreground/55 mb-2">
            <span>Progress</span>
            <span>{progressPercent}% complete</span>
          </div>
          <div className="h-2 rounded-full bg-foreground/10 overflow-hidden">
            <div 
              className="h-full bg-[var(--brand)] transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="rounded-none bg-muted mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="deliverables">Deliverables</TabsTrigger>
          <TabsTrigger value="approvals">
            Approvals
            {pendingApprovals.length > 0 && (
              <span className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--brand)] text-xs text-white">
                {pendingApprovals.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="outcomes">Outcomes</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="space-y-6">
            {/* Next Action Card */}
            <Card className="rounded-3xl border-[var(--brand)]/30 bg-[var(--brand)]/5 p-6 backdrop-blur-2xl">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--brand)]/10 text-[var(--brand)]">
                  <ChevronRight className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">Recommended Next Step</div>
                  <div className="mt-1 text-lg font-semibold text-foreground">{customerData.nextAction}</div>
                  <Button 
                    className="mt-4 h-10 rounded-none bg-[var(--brand)] text-white hover:bg-[var(--brand)]"
                    onClick={() => setActiveTab("deliverables")}
                  >
                    View Deliverables
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>

            {/* KPI Cards */}
            <div className="grid gap-4 md:grid-cols-4">
              <Card className="rounded-2xl border-border bg-foreground/5 p-5 backdrop-blur-2xl">
                <div className="text-xs uppercase tracking-wide text-foreground/55">Opportunity Score</div>
                <div className="mt-2 text-3xl font-semibold text-[var(--brand)]">{customerData.opportunityScore}</div>
                <div className="mt-1 text-xs text-foreground/45">Based on intake analysis</div>
              </Card>
              <Card className="rounded-2xl border-border bg-foreground/5 p-5 backdrop-blur-2xl">
                <div className="text-xs uppercase tracking-wide text-foreground/55">Workflows Identified</div>
                <div className="mt-2 text-3xl font-semibold text-foreground">{customerData.workflowsIdentified}</div>
                <div className="mt-1 text-xs text-foreground/45">High-ROI opportunities</div>
              </Card>
              <Card className="rounded-2xl border-border bg-foreground/5 p-5 backdrop-blur-2xl">
                <div className="text-xs uppercase tracking-wide text-foreground/55">Prioritized</div>
                <div className="mt-2 text-3xl font-semibold text-foreground">{customerData.workflowsPrioritized}</div>
                <div className="mt-1 text-xs text-foreground/45">Ready for implementation</div>
              </Card>
              <Card className="rounded-2xl border-border bg-foreground/5 p-5 backdrop-blur-2xl">
                <div className="text-xs uppercase tracking-wide text-foreground/55">Open Approvals</div>
                <div className="mt-2 text-3xl font-semibold text-foreground">{pendingApprovals.length}</div>
                <div className="mt-1 text-xs text-foreground/45">Awaiting your review</div>
              </Card>
            </div>

            {/* Milestones */}
            <Card className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl">
              <div className="text-sm font-semibold text-foreground mb-4">Your Journey</div>
              <div className="relative">
                {/* Progress line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-foreground/10" />
                <div 
                  className="absolute left-4 top-0 w-0.5 bg-[var(--brand)] transition-all"
                  style={{ height: `${(currentMilestoneIdx / (milestones.length - 1)) * 100}%` }}
                />
                
                <div className="space-y-4">
                  {milestones.map((m, idx) => (
                    <div key={idx} className="flex items-center gap-4 pl-1">
                      <div className={[
                        "relative z-10 flex h-7 w-7 items-center justify-center rounded-full border-2",
                        m.completed 
                          ? "border-[var(--brand)] bg-[var(--brand)]" 
                          : m.current
                          ? "border-[var(--brand)] bg-background"
                          : "border-border bg-background"
                      ].join(" ")}>
                        {m.completed && <Check className="h-4 w-4 text-white" />}
                        {m.current && <div className="h-2 w-2 rounded-full bg-[var(--brand)]" />}
                      </div>
                      <div className="flex-1">
                        <div className={[
                          "text-sm",
                          m.completed || m.current ? "text-foreground" : "text-foreground/45"
                        ].join(" ")}>
                          {m.label}
                        </div>
                        {m.date && (
                          <div className="text-xs text-foreground/35">{m.date}</div>
                        )}
                      </div>
                      {m.current && (
                        <Badge className="rounded-full bg-[var(--brand)]/20 text-[var(--brand)] border-[var(--brand)]/30">
                          Current
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Deliverables Tab */}
        <TabsContent value="deliverables">
          <div className="space-y-4">
            <div className="text-sm text-foreground/60 mb-4">
              Your deliverables and artifacts from the 589 Labs engagement.
            </div>
            
            {deliverables.map((d) => (
              <Card 
                key={d.id}
                className={[
                  "rounded-2xl border-border bg-foreground/5 p-5 backdrop-blur-2xl transition-colors",
                  d.status === "available" ? "hover:bg-foreground/10 cursor-pointer" : ""
                ].join(" ")}
              >
                <div className="flex items-start gap-4">
                  <div className={[
                    "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl",
                    d.status === "available" 
                      ? "bg-emerald-500/10 text-emerald-400"
                      : d.status === "in_progress"
                      ? "bg-[#007a9e]/10 dark:bg-yellow-500/10 text-[#007a9e] dark:text-yellow-400"
                      : "bg-foreground/5 text-foreground/35"
                  ].join(" ")}>
                    <FileText className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold text-foreground">{d.title}</div>
                      {d.status === "available" && (
                        <Badge className="rounded-full bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                          Available
                        </Badge>
                      )}
                      {d.status === "in_progress" && (
                        <Badge className="rounded-full bg-[#007a9e]/20 dark:bg-yellow-500/20 text-[#007a9e] dark:text-yellow-400 border-[#007a9e]/30 dark:border-yellow-500/30">
                          In Progress
                        </Badge>
                      )}
                      {d.status === "locked" && (
                        <Badge className="rounded-full bg-foreground/10 text-foreground/45 border-border">
                          <Lock className="h-3 w-3 mr-1" />
                          Locked
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-foreground/60">{d.description}</div>
                    {d.date && (
                      <div className="mt-2 text-xs text-foreground/35">Published {d.date}</div>
                    )}
                  </div>
                  {d.status === "available" && (
                    <Button
                      variant="secondary"
                      className="h-10 rounded-none border-border bg-foreground/5 text-foreground hover:bg-foreground hover:text-background"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  )}
                </div>
              </Card>
            ))}

            {/* Empty state guidance */}
            <Card className="rounded-2xl border-border bg-muted p-6 mt-6">
              <div className="text-center">
                <div className="text-sm text-foreground/55">
                  More deliverables will appear here as you progress through your engagement.
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Approvals Tab */}
        <TabsContent value="approvals">
          <div className="space-y-4">
            {pendingApprovals.length === 0 ? (
              <Card className="rounded-3xl border-border bg-foreground/5 p-8 backdrop-blur-2xl text-center">
                <div className="flex flex-col items-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-400">
                    <Check className="h-8 w-8" />
                  </div>
                  <div className="mt-4 text-lg font-semibold text-foreground">All caught up!</div>
                  <div className="mt-2 text-sm text-foreground/55">
                    No pending approvals at this time. We'll notify you when something needs your attention.
                  </div>
                </div>
              </Card>
            ) : (
              <>
                <div className="text-sm text-foreground/60 mb-4">
                  Review and approve items from your 589 Labs team.
                </div>
                
                {pendingApprovals.map((a) => (
                  <Card 
                    key={a.id}
                    className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl"
                  >
                    <div className="flex items-start gap-4">
                      <div className={[
                        "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl",
                        a.priority === "high" 
                          ? "bg-[var(--brand)]/10 text-[var(--brand)]"
                          : "bg-foreground/10 text-foreground/70"
                      ].join(" ")}>
                        <Clock className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="text-base font-semibold text-foreground">{a.title}</div>
                          {a.priority === "high" && (
                            <Badge className="rounded-full bg-[var(--brand)]/20 text-[var(--brand)] border-[var(--brand)]/30">
                              Action Needed
                            </Badge>
                          )}
                        </div>
                        <div className="mt-2 text-sm text-foreground/60">{a.description}</div>
                        <div className="mt-2 text-xs text-foreground/35">
                          Requested by {a.requestedBy} · {a.date}
                        </div>
                        
                        <div className="mt-4 flex gap-2">
                          <Button
                            className="h-10 rounded-none bg-[var(--brand)] text-white hover:bg-[var(--brand)]"
                            onClick={() => {
                              toast.success("Approval submitted", {
                                description: "Your approval has been recorded."
                              });
                            }}
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            variant="secondary"
                            className="h-10 rounded-none border-border bg-foreground/5 text-foreground hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30"
                            onClick={() => {
                              toast.message("Request changes", {
                                description: "A comment field will open shortly."
                              });
                            }}
                          >
                            <X className="h-4 w-4 mr-2" />
                            Request Changes
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </>
            )}
          </div>
        </TabsContent>

        {/* Outcomes Tab */}
        <TabsContent value="outcomes">
          <div className="space-y-6">
            <div className="text-sm text-foreground/60 mb-4">
              Track the measurable impact of your workflow automations.
            </div>

            {/* KPI Trends */}
            <div className="grid gap-4 md:grid-cols-2">
              <Card className="rounded-2xl border-border bg-foreground/5 p-5 backdrop-blur-2xl">
                <div className="text-sm font-semibold text-foreground mb-4">Hours Saved per Week</div>
                <div className="flex items-end gap-4">
                  <div>
                    <div className="text-xs text-foreground/55">Baseline</div>
                    <div className="text-2xl font-semibold text-foreground/45">0</div>
                  </div>
                  <div className="flex-1 text-center">
                    <TrendingUp className="h-6 w-6 text-emerald-400 mx-auto" />
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-foreground/55">Current</div>
                    <div className="text-2xl font-semibold text-emerald-400">12</div>
                  </div>
                </div>
                <div className="mt-4 text-center text-sm text-emerald-400">
                  +12 hours saved weekly
                </div>
              </Card>

              <Card className="rounded-2xl border-border bg-foreground/5 p-5 backdrop-blur-2xl">
                <div className="text-sm font-semibold text-foreground mb-4">Error Rate Reduction</div>
                <div className="flex items-end gap-4">
                  <div>
                    <div className="text-xs text-foreground/55">Baseline</div>
                    <div className="text-2xl font-semibold text-foreground/45">8%</div>
                  </div>
                  <div className="flex-1 text-center">
                    <TrendingUp className="h-6 w-6 text-emerald-400 mx-auto" />
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-foreground/55">Current</div>
                    <div className="text-2xl font-semibold text-emerald-400">2%</div>
                  </div>
                </div>
                <div className="mt-4 text-center text-sm text-emerald-400">
                  75% reduction in errors
                </div>
              </Card>
            </div>

            {/* Recommendations */}
            <Card className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl">
              <div className="text-sm font-semibold text-foreground mb-4">Expansion Opportunities</div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/60 p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10 text-purple-400">
                    <Target className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-foreground">Invoice Processing Automation</div>
                    <div className="text-xs text-foreground/45">Est. 8 additional hours saved/week</div>
                  </div>
                  <Badge className="rounded-full bg-purple-500/20 text-purple-400 border-purple-500/30">
                    Recommended
                  </Badge>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/60 p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-400">
                    <Target className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-foreground">Customer Onboarding Flow</div>
                    <div className="text-xs text-foreground/45">Est. 5 additional hours saved/week</div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-border text-center">
                <div className="text-sm text-foreground/55">
                  Ready to expand? Talk to your consultant about adding workflows to your retainer.
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
